import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetSiteSpecificData } from "../app/store/GlobalActions";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "../app/store/RequestState";
import * as Api from "./camerasApi";
import { ICameraId } from "./camerasModels";
import { LoadingState } from "../app/LoadingState";
import { ICameraMetadata } from "../sites/SiteModels";

export const getKey = (cameraId: ICameraId, siteId: string) =>
  JSON.stringify([siteId, cameraId.agentId, cameraId.id]);

export const getConnectionKey = (cameraId: number, siteId: string) => JSON.stringify([siteId, cameraId]);

interface CameraInventoryState extends RequestState {
  cameras?: ICameraMetadata[];
}
interface CameraConnectionState extends RequestState {
  agentId?: number;
}
interface CameraReadState extends RequestState {
  commandState: LoadingState;
}
interface CameraSnapshotState extends CameraReadState {
  image?: string;
  sentAt?: number;
}
interface ICamerasStoreState {
  inventory: Record<string, CameraInventoryState>;
  connections: Record<string, CameraConnectionState>;
  healthCheck: Record<string, CameraReadState>;
  snapshots: Record<string, CameraSnapshotState>;
  movements: Record<string, RequestState & { CommandState: LoadingState }>;
}
export const defaultProperties: ICamerasStoreState = {
  inventory: {},
  connections: {},
  healthCheck: {},
  snapshots: {},
  movements: {},
};

export const camerasSlice = createSlice({
  name: "cameras",
  initialState: defaultProperties,
  reducers: {
    retryCameraConnection: (
      state,
      action: PayloadAction<{ siteId: string; cameraId: number; agentId?: number }>
    ) => {
      const { siteId, cameraId } = action.payload;
      const connectionKey = getConnectionKey(cameraId, siteId);
      const connection = state.connections[connectionKey];
      const agentId = action.payload.agentId ?? connection?.agentId;
      if (connection) {
        state.connections[connectionKey] = {
          agentId: connection.agentId,
          loadingState: LoadingState.None,
        };
      }
      if (agentId != null) {
        const key = getKey({ id: cameraId, agentId }, siteId);
        delete state.healthCheck[key];
        const snapshot = state.snapshots[key];
        if (snapshot) {
          state.snapshots[key] = {
            image: snapshot.image,
            commandState: LoadingState.None,
            loadingState: LoadingState.None,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetSiteSpecificData, () => defaultProperties)
      .addCase(fetchCameraInventory.pending, (state, action) => {
        const current = (state.inventory[action.meta.arg] ??= { loadingState: LoadingState.None });
        beginRequest(current, action.meta.requestId);
      })
      .addCase(fetchCameraInventory.fulfilled, (state, action) => {
        const current = state.inventory[action.meta.arg];
        if (current && completeRequest(current, action.meta.requestId)) {
          current.cameras = action.payload;
        }
      })
      .addCase(fetchCameraInventory.rejected, (state, action) => {
        const current = state.inventory[action.meta.arg];
        if (current) {
          rejectRequest(current, action);
        }
      })
      .addCase(fetchCameraConnection.pending, (state, action) => {
        const key = getConnectionKey(action.meta.arg.cameraId, action.meta.arg.siteId);
        const current = (state.connections[key] ??= { loadingState: LoadingState.None });
        beginRequest(current, action.meta.requestId);
        current.error = current.errorBeforeRequest;
      })
      .addCase(fetchCameraConnection.fulfilled, (state, action) => {
        const current =
          state.connections[getConnectionKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (current && completeRequest(current, action.meta.requestId)) {
          current.agentId = action.payload.agentId;
        }
      })
      .addCase(fetchCameraConnection.rejected, (state, action) => {
        const current =
          state.connections[getConnectionKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (current) {
          rejectRequest(current, action);
        }
      })
      .addCase(healthCheck.pending, (state, action) => {
        const key = getKey(action.meta.arg.cameraId, action.meta.arg.siteId);
        const current = (state.healthCheck[key] ??= {
          commandState: LoadingState.None,
          loadingState: LoadingState.None,
        });
        beginRequest(current, action.meta.requestId);
        // A retry must not clear the last known offline health result.
        current.error = current.errorBeforeRequest;
        current.commandState = current.loadingState;
      })
      .addCase(healthCheck.fulfilled, (state, action) => {
        const current = state.healthCheck[getKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (!current || !completeRequest(current, action.meta.requestId)) {
          return;
        }
        current.error = action.payload.error;
        current.loadingState = action.payload.error == null ? LoadingState.Complete : LoadingState.Error;
        current.commandState = current.loadingState;
      })
      .addCase(healthCheck.rejected, (state, action) => {
        const current = state.healthCheck[getKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (current && rejectRequest(current, action)) {
          current.commandState = current.loadingState;
        }
      })
      .addCase(fetchSnapshot.pending, (state, action) => {
        const key = getKey(action.meta.arg.cameraId, action.meta.arg.siteId);
        const current = (state.snapshots[key] ??= {
          commandState: LoadingState.None,
          loadingState: LoadingState.None,
        });
        beginRequest(current, action.meta.requestId);
        current.commandState = current.loadingState;
        current.error = current.errorBeforeRequest;
        current.sentAt = Date.now();
      })
      .addCase(fetchSnapshot.fulfilled, (state, action) => {
        const current = state.snapshots[getKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (current && completeRequest(current, action.meta.requestId)) {
          current.image = action.payload;
          current.commandState = current.loadingState;
        }
      })
      .addCase(fetchSnapshot.rejected, (state, action) => {
        const current = state.snapshots[getKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (current && rejectRequest(current, action)) {
          current.commandState = current.loadingState;
        }
      })
      .addCase(moveCamera.pending, (state, action) => {
        const key = getKey(action.meta.arg.cameraId, action.meta.arg.siteId);
        const current = (state.movements[key] ??= {
          CommandState: LoadingState.None,
          loadingState: LoadingState.None,
        });
        beginRequest(current, action.meta.requestId);
        current.CommandState = current.loadingState;
      })
      .addCase(moveCamera.fulfilled, (state, action) => {
        const current = state.movements[getKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (current && completeRequest(current, action.meta.requestId)) {
          current.CommandState = current.loadingState;
        }
      })
      .addCase(moveCamera.rejected, (state, action) => {
        const current = state.movements[getKey(action.meta.arg.cameraId, action.meta.arg.siteId)];
        if (current && rejectRequest(current, action)) {
          current.CommandState = current.loadingState;
        }
      });
  },
});
export const camerasReducer = camerasSlice.reducer;
export const { retryCameraConnection } = camerasSlice.actions;
export const fetchCameraInventory = createAsyncThunk(
  "cameras/fetchInventory",
  (siteId: string, { signal }) => Api.getSiteCamerasMetadata(siteId, signal)
);
export const fetchCameraConnection = createAsyncThunk(
  "cameras/fetchConnection",
  (args: { siteId: string; cameraId: number }, { signal }) =>
    Api.resolveCameraConnection(args.siteId, args.cameraId, signal)
);
export const fetchSnapshot = createAsyncThunk(
  "cameras/fetchSnapshot",
  (args: { siteId: string; cameraId: ICameraId; initial: boolean }, { signal }) =>
    Api.fetchSnapshots(args.siteId, args.cameraId, signal)
);
export const healthCheck = createAsyncThunk(
  "cameras/healthCheck",
  (args: { siteId: string; cameraId: ICameraId; isInitial: boolean }, { signal }) =>
    Api.healthCheck(args.siteId, args.cameraId, signal)
);
export const moveCamera = createAsyncThunk(
  "cameras/move",
  (
    args: { siteId: string; cameraId: ICameraId; x: number; y: number; milliseconds: number },
    { signal }
  ) => Api.moveCamera(args.siteId, args.cameraId, args.x, args.y, args.milliseconds, signal)
);
